import { UserIdentityType } from '@/services/UserIdentitiesService';
import { normalizePersonName } from '@/utils/personNameRules';
import { isMainNewDraft } from '@/screen/Identities/holderIdentityDraft';

// Reglas de identificación del cliente en la compra de la suscripción.
// Mismo criterio que el backend (PATCH users/{id}): si el usuario no tiene ningún interviniente se crea el principal
// y, si su ÚNICO interviniente es el principal en NEW (el que nace vacío con el registro en DruID, GetSoleMainNewDraft),
// se rellena; en ambos casos con nombre, apellidos, email, prefijo y teléfono (el segundo apellido es opcional).
// Con cualquier otro interviniente se ignora la identidad. Los teléfonos de firma y de operaciones no se envían.

export type IdentityPatch =
{
    id: number
    mainUserIdentity:
    {
        firstName: string
        lastName: string
        secondLastName?: string
        email: string
        countryPrefix: string
        phone: string
    }
};

// Código con el que el backend rechaza (400) el alta o el relleno del interviniente principal por faltar datos obligatorios
export const MainUserIdentityRequiredFieldsCode = "MAIN_USER_IDENTITY_REQUIRED_FIELDS";

// Código con el que el backend rechaza (400) un nombre o apellido que no cumple PersonNameRules
export const InvalidNameFormatCode = "INVALID_NAME_FORMAT";

const MissingFieldLabels: Record<string, string> =
{
    firstName: "Nombre",
    lastName: "Primer apellido",
    secondLastName: "Segundo apellido",
    email: "Correo electrónico",
    countryPrefix: "Prefijo (país)",
    phone: "Número teléfono móvil"
};

/**
 * Principal en NEW cuando es el ÚNICO interviniente del usuario (mismo criterio que GetSoleMainNewDraft del backend):
 * la compra lo rellena en lugar de crear otro. Con cualquier otra combinación devuelve undefined.
 */
export const getSoleMainNewDraft = (identities: UserIdentityType[] | null | undefined): UserIdentityType | undefined =>
{
    if(!Array.isArray(identities) || identities.length !== 1)
    {
        return undefined;
    }

    const sole = identities[0];
    return sole?.main === true && isMainNewDraft(sole.status) ? sole : undefined;
};

/**
 * Se envía la identidad cuando ya se conocen los intervinientes y o no hay ninguno o el único es el principal en NEW.
 * Si aún no se han cargado (undefined/null) no se envía nada.
 */
export const shouldSendIdentity = (identities: UserIdentityType[] | null | undefined): boolean =>
{
    return Array.isArray(identities) && (identities.length === 0 || getSoleMainNewDraft(identities) !== undefined);
};

/**
 * Los datos se muestran en modo lectura cuando el usuario ya tiene intervinientes que la compra no puede tocar
 * (cualquiera salvo el principal en NEW como único interviniente).
 */
export const isIdentityReadOnly = (identities: UserIdentityType[] | null | undefined): boolean =>
{
    return Array.isArray(identities) && identities.length > 0 && !shouldSendIdentity(identities);
};

/**
 * Identidad que se muestra en modo lectura: el interviniente principal o, si no lo hay, la del perfil.
 */
export const selectDisplayIdentity = (identities: UserIdentityType[] | null | undefined, profileIdentity: UserIdentityType | null | undefined): UserIdentityType | undefined =>
{
    const mainIdentity = Array.isArray(identities) ? identities.find((identity) => identity.main) : undefined;
    return mainIdentity ?? profileIdentity ?? undefined;
};

function firstWithContent<T>(draftValue: T | null | undefined, profileValue: T | null | undefined): T | undefined
{
    const hasContent = (value: T | null | undefined): boolean => value !== undefined && value !== null && String(value).trim() !== '';
    if(hasContent(draftValue))
    {
        return draftValue as T;
    }
    return hasContent(profileValue) ? profileValue as T : undefined;
}

/**
 * Identidad con la que se precarga el formulario editable.
 * Sin principal en NEW es la del perfil (como hasta ahora). Con él, cada dato es el del principal o, si lo tiene vacío,
 * el del perfil; el email solo se toma del principal (si no tiene, el formulario usa el del usuario de acceso).
 */
export const selectEditableIdentity = (identities: UserIdentityType[] | null | undefined, profileIdentity: UserIdentityType | null | undefined): UserIdentityType | undefined =>
{
    const draft = getSoleMainNewDraft(identities);
    if(draft === undefined)
    {
        // Alta sin intervinientes: el email es siempre el del usuario de acceso, nunca el que traiga el perfil
        return profileIdentity ? { ...profileIdentity, email: undefined } : undefined;
    }

    return {
        id: draft.id,
        main: draft.main,
        status: draft.status,
        firstName: firstWithContent(draft.firstName, profileIdentity?.firstName),
        lastName: firstWithContent(draft.lastName, profileIdentity?.lastName),
        secondLastName: firstWithContent(draft.secondLastName, profileIdentity?.secondLastName),
        birthday: firstWithContent(draft.birthday, profileIdentity?.birthday),
        email: firstWithContent(draft.email, undefined),
        countryPrefix: firstWithContent(draft.countryPrefix, profileIdentity?.countryPrefix),
        phone: firstWithContent(draft.phone, profileIdentity?.phone)
    };
};

/**
 * Construye el cuerpo del PATCH users/{id} con lo único que el backend usa al crear o rellenar el interviniente
 * principal.
 */
export const buildIdentityPatch = (userId: number, formData: UserIdentityType): IdentityPatch =>
{
    // Alta o relleno del principal vacío: los nombres se envían ya limpios (PersonNameRules, igual que el backend).
    const mainUserIdentity: IdentityPatch['mainUserIdentity'] =
    {
        firstName: normalizePersonName(formData.firstName),
        lastName: normalizePersonName(formData.lastName),
        email: (formData.email ?? '').trim(),
        countryPrefix: (formData.countryPrefix ?? '').trim(),
        // Sin espacios internos, coherente con validatePhone
        phone: (formData.phone ?? '').replace(/\s+/g, '')
    };

    // El segundo apellido es opcional: si queda vacío no se envía la clave
    const secondLastName = normalizePersonName(formData.secondLastName);
    if(secondLastName !== '')
    {
        mainUserIdentity.secondLastName = secondLastName;
    }

    return {
        id: userId,
        mainUserIdentity
    };
};

/**
 * Si el error del PATCH es un 400 por datos obligatorios que faltan o por nombre/apellidos con formato no válido,
 * devuelve un mensaje comprensible (el del backend más los campos afectados). Para cualquier otro error devuelve
 * undefined.
 */
export const getIdentityPatchErrorMessage = (error: any): string | undefined =>
{
    const response = error?.response;
    const data = response?.data;
    if(response?.status !== 400)
    {
        return undefined;
    }

    if(data?.code === MainUserIdentityRequiredFieldsCode)
    {
        return buildFieldsMessage(data.message, "Faltan datos obligatorios para identificar al cliente.", data.missingFields);
    }
    if(data?.code === InvalidNameFormatCode)
    {
        return buildFieldsMessage(data.message, "El nombre y los apellidos solo pueden contener letras, espacios, guiones y apóstrofos.", data.invalidFields);
    }
    return undefined;
};

const buildFieldsMessage = (message: unknown, fallbackMessage: string, fields: unknown): string =>
{
    const baseMessage: string = typeof message === 'string' && message.trim() !== '' ? message.trim() : fallbackMessage;

    const fieldLabels: string[] = Array.isArray(fields)
        ? fields.filter((field: unknown) => typeof field === 'string').map((field: string) => MissingFieldLabels[field] ?? field)
        : [];

    return fieldLabels.length > 0 ? `${baseMessage} Revisa: ${fieldLabels.join(", ")}.` : baseMessage;
};
